import React from 'react';
import { ELEMENT_TABLE } from '@udecode/plate';
import { findNodePath, useEditorRef, useElement, getPluginOptions } from '@udecode/plate-common';
import {
  resizeLengthClampStatic,
} from '@udecode/plate-resizable';
import {
  useOverrideColSize,
  useOverrideMarginLeft,
  useOverrideRowSize,
  useTableStore,
  setTableColSize,
  setTableMarginLeft,
  setTableRowSize,
  useTableColSizes,
  roundCellSizeToStep,
} from '@udecode/plate-table';

import { KEY_DOCUMENT_SETTINGS } from '../document-settings/createDocumentSettingsPlugin';

/* eslint-disable no-shadow */
export const useTableCellElementResizable = ({
  colIndex,
  colSpan,
  disableMarginLeft,
  rowIndex,
  stepX,
  stepY,
}) => {
  const editor = useEditorRef();
  const element = useElement();
  const tableElement = useElement(ELEMENT_TABLE);
  const { minColumnWidth = 0 } = getPluginOptions(
    editor,
    ELEMENT_TABLE,
  );

  const { innerWidth } = getPluginOptions(editor, KEY_DOCUMENT_SETTINGS);

  let initialWidth;

  if (colSpan > 1) {
    initialWidth = tableElement.colSizes?.[colIndex];
  }

  const [hoveredColIndex, setHoveredColIndex] =
    useTableStore().use.hoveredColIndex();

  const colSizesWithoutOverrides = useTableColSizes(tableElement, {
    disableOverrides: true,
  });
  const { marginLeft = 0 } = tableElement;

  const overrideColSize = useOverrideColSize();
  const overrideRowSize = useOverrideRowSize();
  const overrideMarginLeft = useOverrideMarginLeft();

  const setColSize = React.useCallback(
    (colIndex, width) => {
      setTableColSize(
        editor,
        { colIndex, width },
        { at: findNodePath(editor, element) },
      );

      // Prevent flickering
      setTimeout(() => overrideColSize(colIndex, null), 0);
    },
    [editor, element, overrideColSize],
  );

  const setRowSize = React.useCallback(
    (rowIndex, height) => {
      setTableRowSize(
        editor,
        { height, rowIndex },
        { at: findNodePath(editor, element) },
      );

      // Prevent flickering
      setTimeout(() => overrideRowSize(rowIndex, null), 0);
    },
    [editor, element, overrideRowSize],
  );

  const setMarginLeft = React.useCallback(
    (marginLeft) => {
      setTableMarginLeft(
        editor,
        { marginLeft },
        { at: findNodePath(editor, element) },
      );

      // Prevent flickering
      setTimeout(() => overrideMarginLeft(null), 0);
    },
    [editor, element, overrideMarginLeft],
  );

  const handleResizeRight = React.useCallback(
    ({ delta, finished, initialSize }) => {
      const nextInitial = colSizesWithoutOverrides[colIndex + 1];

      // Magic number 20 is to account for table paddings, which this callback
      // doesn't account for. Just the inner width.
      const currentInitial = colSpan ?
        colSizesWithoutOverrides[colIndex] :
        initialSize + 20;


      const complement = (width) =>
        currentInitial + nextInitial - width;

      // If moving handle right of the last column in a table, calculate all widths except for last column.
      // Then ensure that new width plus the rest does not exceed document inner width.
      const maxColumnWidth = (!nextInitial && innerWidth) ?
        innerWidth - colSizesWithoutOverrides.slice(0, -1).reduce((prev, value) => prev + value, 0) - marginLeft :
        undefined;

      const currentNew = roundCellSizeToStep(
        resizeLengthClampStatic(currentInitial + delta, {
          max: nextInitial ? complement(minColumnWidth) : maxColumnWidth - 1,
          min: minColumnWidth,
        }),
        stepX,
      );

      const nextNew = nextInitial ? complement(currentNew) : undefined;

      const fn = finished ? setColSize : overrideColSize;
      fn(colIndex, currentNew);

      if (nextNew) fn(colIndex + 1, nextNew);
    },
    [
      colIndex,
      colSizesWithoutOverrides,
      minColumnWidth,
      overrideColSize,
      setColSize,
      stepX,
    ],
  );

  const handleResizeBottom = React.useCallback(
    (event) => {
      const newHeight = roundCellSizeToStep(
        event.initialSize + event.delta,
        stepY,
      );

      if (event.finished) {
        setRowSize(rowIndex, newHeight);
      } else {
        overrideRowSize(rowIndex, newHeight);
      }
    },
    [overrideRowSize, rowIndex, setRowSize, stepY],
  );

  const handleResizeLeft = React.useCallback(
    (event) => {
      const initial = colSizesWithoutOverrides[colIndex];

      const complement = (width) => initial + marginLeft - width;

      const newMargin = roundCellSizeToStep(
        resizeLengthClampStatic(marginLeft + event.delta, {
          max: complement(minColumnWidth),
          min: 0,
        }),
        stepX,
      );

      const newWidth = complement(newMargin);

      if (event.finished) {
        setMarginLeft(newMargin);
        setColSize(colIndex, newWidth);
      } else {
        overrideMarginLeft(newMargin);
        overrideColSize(colIndex, newWidth);
      }
    },
    [
      colIndex,
      colSizesWithoutOverrides,
      marginLeft,
      minColumnWidth,
      overrideColSize,
      overrideMarginLeft,
      setColSize,
      setMarginLeft,
      stepX,
    ],
  );

  const getHandleHoverProps = (colIndex) => ({
    onHover: () => {
      if (hoveredColIndex === null) {
        setHoveredColIndex(colIndex);
      }
    },
    onHoverEnd: () => {
      if (hoveredColIndex === colIndex) {
        setHoveredColIndex(null);
      }
    },
  });

  const hasLeftHandle = colIndex === 0 && !disableMarginLeft;

  return {
    bottomProps: {
      options: {
        direction: 'bottom',
        onResize: handleResizeBottom,
      },
    },
    hiddenLeft: !hasLeftHandle,
    leftProps: {
      options: {
        direction: 'left',
        onResize: handleResizeLeft,
        ...getHandleHoverProps(-1),
      },
    },
    rightProps: {
      options: {
        direction: 'right',
        initialSize: initialWidth,
        onResize: handleResizeRight,
        ...getHandleHoverProps(colIndex),
      },
    },
  };
  /* eslint-enable no-shadow */
};